import { useState, useRef, useCallback, useMemo } from "react";
import { ChevronLeft, ChevronRight, Copy, Check, Loader2, ArrowUp, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

// Same gradient pool as AI chat bubbles (purple)
const BUBBLE_GRADIENTS = [
  "linear-gradient(135deg, rgba(125,92,240,0.09) 0%, rgba(110,78,220,0.04) 100%)",
  "linear-gradient(150deg, rgba(130,95,245,0.08) 0%, rgba(115,82,228,0.04) 100%)",
  "linear-gradient(120deg, rgba(120,88,235,0.09) 0%, rgba(128,95,240,0.04) 100%)",
  "linear-gradient(140deg, rgba(128,94,242,0.08) 0%, rgba(105,75,218,0.04) 100%)",
  "linear-gradient(160deg, rgba(135,98,248,0.08) 0%, rgba(118,85,232,0.04) 100%)",
];

function pickGradient(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  return BUBBLE_GRADIENTS[Math.abs(hash) % BUBBLE_GRADIENTS.length]!;
}

interface SvgBuilderProps {
  data: {
    svg: string;
    prompt?: string;
  };
  onInputFocus?: (focused: boolean) => void;
}

/** Detect the blank canvas SVG (no real content yet). */
function isEmptyCanvas(svg: string): boolean {
  return !svg || /^<svg[^>]*>\s*<rect[^>]*fill="white"[^>]*\/>\s*<\/svg>$/s.test(svg);
}

export function SvgBuilder({ data, onInputFocus }: SvgBuilderProps) {
  const [versions, setVersions] = useState<string[]>(
    isEmptyCanvas(data.svg) ? [] : [data.svg],
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const isEmpty = versions.length === 0;
  const currentSvg = isEmpty ? null : (versions[currentIndex] ?? versions[0]);
  const canGoBack = currentIndex > 0;
  const canGoForward = currentIndex < versions.length - 1;

  const handleIterate = useCallback(async () => {
    const prompt = input.trim();
    if (!prompt || loading) return;

    setLoading(true);
    try {
      if (isEmpty || !currentSvg) {
        // First generation — create from scratch
        const result = await api.svg.generate(prompt);
        if (result.svg && !isEmptyCanvas(result.svg)) {
          setVersions([result.svg]);
          setCurrentIndex(0);
          setInput("");
        }
      } else {
        // Iterate on current version
        const result = await api.svg.iterate(currentSvg, prompt);
        if (result.svg) {
          const newVersions = [...versions.slice(0, currentIndex + 1), result.svg];
          setVersions(newVersions);
          setCurrentIndex(newVersions.length - 1);
          setInput("");
        }
      }
    } catch {
      // Silently fail — the SVG stays as-is
    } finally {
      setLoading(false);
    }
  }, [input, loading, currentSvg, isEmpty, versions, currentIndex]);

  const handleClear = useCallback(() => {
    setVersions([]);
    setCurrentIndex(0);
    setInput("");
  }, []);

  const handleCopy = useCallback(async () => {
    if (!currentSvg) return;
    try {
      await navigator.clipboard.writeText(currentSvg);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may not be available
    }
  }, [currentSvg]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleIterate();
    }
  };

  const handleInputFocus = useCallback(() => {
    onInputFocus?.(true);
    // Wait for keyboard to fully open, then compute exact scroll position
    // so the widget's bottom sits just above the keyboard (like the main input)
    setTimeout(() => {
      const card = cardRef.current;
      const scroller = card?.closest("[data-scroll-container]") as HTMLElement | null;
      if (!card || !scroller) return;

      const vv = window.visualViewport;
      // Visible area = visualViewport height (excludes keyboard)
      const visibleHeight = vv?.height ?? window.innerHeight;
      // Keyboard gap: 8px bottom padding (matches main input's keyboard-open position)
      const gap = 16;

      const cardRect = card.getBoundingClientRect();
      const scrollerRect = scroller.getBoundingClientRect();

      // Where the card's bottom currently is relative to the scroller
      const cardBottomInScroller = cardRect.bottom - scrollerRect.top + scroller.scrollTop;
      // Where we want the card's bottom to land: at (visibleHeight - gap) from viewport top,
      // translated into scroller scroll position
      const targetScrollTop = cardBottomInScroller - visibleHeight + gap;

      scroller.scrollTo({ top: Math.max(0, targetScrollTop), behavior: "smooth" });
    }, 350);
  }, [onInputFocus]);

  const handleInputBlur = useCallback(() => {
    onInputFocus?.(false);
  }, [onInputFocus]);

  const gradient = useMemo(
    () => pickGradient(data.prompt ?? "svg-builder"),
    [data.prompt],
  );

  return (
    <div ref={cardRef} className="overflow-hidden max-w-md animate-slide-up rounded-3xl rounded-bl-lg" style={{ background: gradient }}>
      {/* SVG Preview */}
      <div className="relative" style={{ background: "#1e1e22" }}>
        {isEmpty ? (
          <div className="flex items-center justify-center p-4 min-h-[200px]">
            <span className="text-sm text-daydra-muted/40">Empty canvas</span>
          </div>
        ) : (
          <div
            className="flex items-center justify-center p-4 min-h-[200px] [&>svg]:max-w-full [&>svg]:max-h-[300px] [&>svg]:w-auto [&>svg]:h-auto"
            dangerouslySetInnerHTML={{ __html: currentSvg ?? "" }}
          />
        )}

        {/* Version navigation overlay */}
        {versions.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1">
            <button
              onClick={() => canGoBack && setCurrentIndex(currentIndex - 1)}
              disabled={!canGoBack}
              className={cn(
                "p-0.5 rounded-full transition-opacity",
                canGoBack ? "text-white hover:bg-white/20" : "text-white/30 cursor-default",
              )}
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-2xs text-white/80 font-mono min-w-[3ch] text-center">
              {currentIndex + 1}/{versions.length}
            </span>
            <button
              onClick={() => canGoForward && setCurrentIndex(currentIndex + 1)}
              disabled={!canGoForward}
              className={cn(
                "p-0.5 rounded-full transition-opacity",
                canGoForward ? "text-white hover:bg-white/20" : "text-white/30 cursor-default",
              )}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-2 border-t border-white/[0.06]">
        {!isEmpty && (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCopy}
              className="gap-1.5"
            >
              {copied ? <Check size={13} /> : <Copy size={13} />}
              {copied ? "Copied" : "Copy SVG"}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleClear}
              className="gap-1.5"
            >
              <Trash2 size={13} />
              Clear
            </Button>
          </>
        )}
        {data.prompt && (
          <span className="text-2xs text-daydra-muted truncate flex-1 text-right">
            {data.prompt}
          </span>
        )}
      </div>

      {/* Input */}
      <div className="flex items-center gap-2 px-3 pb-3 pt-1">
        <div className="flex-1 min-w-0 bg-black/30 rounded-2xl px-3 ring-1 ring-white/[0.04] transition-shadow duration-normal focus-within:shadow-[0_0_24px_rgba(134,87,241,0.18)]">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder={isEmpty ? "Describe your SVG..." : "Describe adjustments..."}
            disabled={loading}
            rows={1}
            className="w-full resize-none bg-transparent py-[9px] text-sm leading-snug text-daydra-text placeholder:text-daydra-muted/50 focus:outline-none disabled:opacity-50"
            style={{ height: "36px", maxHeight: "80px", overflowY: "hidden" }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = "36px";
              const h = Math.min(target.scrollHeight, 80);
              target.style.height = h + "px";
              target.style.overflowY = h >= 80 ? "auto" : "hidden";
            }}
          />
        </div>
        <button
          onMouseDown={(e) => { e.preventDefault(); handleIterate(); }}
          onTouchEnd={(e) => { e.preventDefault(); handleIterate(); }}
          disabled={!input.trim() || loading}
          className={cn(
            "flex items-center justify-center w-9 rounded-full transition-all duration-normal shrink-0",
            input.trim() && !loading
              ? "text-daydra-bg hover:brightness-110 scale-100"
              : "text-daydra-muted/40 scale-95 bg-transparent",
            "disabled:opacity-30 disabled:scale-95",
          )}
          style={{
            height: "36px",
            ...(input.trim() && !loading ? { background: "var(--brand-gradient)" } : {}),
          }}
        >
          {loading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <ArrowUp size={16} strokeWidth={2.5} />
          )}
        </button>
      </div>
    </div>
  );
}
